import { LoginInput } from "../models/LoginInput";
import { HttpService } from "../../../Shared/Utils/HttpService";
import { LoginResponse } from "../models/LoginResponse";
import { RolesEnum } from "../../../Shared/enums/RoleEnum";
import { getCurrentPath } from "../../../Scaffold/RouteEvent";
import { RegisterInput } from "../models/RegisterInput";

export class AuthService {
  static async login(loginInput: LoginInput) {
    const response = await HttpService.request({
      url: "/auth/login",
      method: "POST",
      data: loginInput,
    });
    if (response && response.accessToken) {
      this.saveUser(response as LoginResponse);
    }
    return response;
  }

  static async reAuthenticate() {
    const response = await HttpService.request({
      url: "/auth/reauthenticate",
      method: "GET",
    });
    if (response && response.accessToken) {
      this.removeLocalUser();
      this.saveUser(response as LoginResponse);
    }
    return response;
  }

  static async getCurrentUser() {
    return await HttpService.request({
      url: "/auth/user",
      method: "GET",
    });
  }

  static async update(user: any) {
    return await HttpService.request({
      url: "/auth/user",
      method: "PUT",
      data: user,
    });
  }

  static async changePassword(oldPassword: string, newPassword: string) {
    const data = {
      oldPassword,
      newPassword,
    };
    return await HttpService.request({
      url: "/auth/change-password",
      method: "POST",
      data,
    });
  }

  static async forgotPassword(email: string) {
    const data = {
      email,
    };
    return await HttpService.request({
      url: "/auth/forgot-password",
      method: "POST",
      data,
    });
  }

  static async resetPassword(token: string, newPassword: string) {
    const data = {
      token,
      newPassword,
    };
    return await HttpService.request({
      url: "/auth/reset-password",
      method: "POST",
      data,
    });
  }

  static async verifyEmail(token: string) {
    const data = {
      token,
    };
    return await HttpService.request({
      url: "/auth/verify-email",
      method: "POST",
      data,
    });
  }

  static async register(registerInput: RegisterInput) {
    return await HttpService.request({
      url: "/auth/register",
      method: "POST",
      data: registerInput,
    });
  }

  static async uploadAvatar(name: string, avatarFile: File) {
    const formData = new FormData();
    formData.append("name", name);
    formData.append("avatar", avatarFile);

    return await HttpService.request({
      url: "/upload",
      method: "POST",
      data: formData,
      contentType: "form-data",
    });
  }

  static async logout() {
    const path = getCurrentPath();
    const authPaths = [
      "/auth/login",
      "/auth/forgot-password",
      "/auth/reset-password",
      "/auth/change-password",
    ];

    if (!authPaths.includes(path)) {
      this.removeLocalUser();
      window.location.href = "/auth/login";
    }
    return await HttpService.request({
      url: "/auth/logout",
      method: "POST",
    });
  }

  static saveUser(user: LoginResponse) {
    localStorage.setItem("oguser", JSON.stringify(user));
  }

  static getLocalUser(): LoginResponse | null {
    const user = localStorage.getItem("oguser");
    return user ? JSON.parse(user) : null;
  }

  static isAuthenticated(): boolean {
    const user = AuthService.getLocalUser();
    return user && user.accessToken ? true : false;
  }

  static getAccessToken(): any | null {
    const user = this.getLocalUser();
    if (!user || !user.accessToken) {
      return null;
    }
    return user.accessToken;
  }

  static removeLocalUser() {
    localStorage.removeItem("oguser");
  }

  static isManagementAllowed() {
    const currentUser: LoginResponse | null = this.getLocalUser();
    let allowedMain = false;
    try {
      allowedMain = currentUser!.user!.roles!.some(
        (role) =>
          role.name?.toLocaleLowerCase() ===
            RolesEnum.ADMIN.toLocaleLowerCase() ||
          role.name?.toLocaleLowerCase() === RolesEnum.STAFF.toLocaleLowerCase()
      );
    } catch (error) {
      allowedMain = false;
    }
    return allowedMain;
  }
}
